import { APP_ROUTES } from '@cpd-app/core-module/constants/routes';

/** Where a visitor stands when they arrive on a page. */
export interface DoorEntryState {
  signedIn: boolean;
  /** The profile has arrived; until then nobody is stopped. */
  profileLoaded: boolean;
  skillsSurveyDone: boolean;
  /** Answered the front door before, on this device or any other. */
  answered: boolean;
}

/** The pages that ARE the front door's home, so never redirected to it. */
const HOME_PATHS = ['/', '/home', APP_ROUTES.CareerPlanning.BuildTarget];

const pathOf = (url: string): string => url.split(/[?#]/)[0] || '/';

/**
 * FD-2. Whether a visitor arriving at `url` meets the front door first.
 *
 * A first-time visitor on a direct link does, and the link is kept as `next`
 * so the door can send them on to it. Signed-in members, anyone who has
 * answered the door or done the Skills Survey, and anyone we cannot decide
 * for yet go straight through: a wrong "no" costs one question, a wrong "yes"
 * would hide the page a counselor sent them to.
 */
export function doorEntry(
  url: string,
  state: DoorEntryState
): { redirect: boolean; next: string | null } {
  const firstTime =
    state.profileLoaded &&
    !state.signedIn &&
    !state.skillsSurveyDone &&
    !state.answered;
  const isHome = HOME_PATHS.includes(pathOf(url));

  return firstTime && !isHome
    ? { redirect: true, next: url }
    : { redirect: false, next: null };
}

/**
 * `next` only ever leads somewhere inside CPD, and never back to home: an
 * open redirect from a query parameter is how a link turns into phishing.
 */
export function safeNext(next: string | null | undefined): string | null {
  if (!next || !next.startsWith('/') || next.startsWith('//')) {
    return null;
  }
  if (next.includes('\\') || HOME_PATHS.includes(pathOf(next))) {
    return null;
  }
  return next;
}
